import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslator } from 'tradera/lang/translate';
import * as RadixPopover from '@radix-ui/react-popover';
import styles from './popover.module.scss';
import { Button } from '../atoms/button/button';
import { Text } from '../atoms/text/text';
import { classNames } from 'tradera/utils/class-names';
import CloseIcon from 'tradera/blueprint/theme/icons2/close.svg?jsx';
import { useIsMounted } from 'tradera/hooks/use-is-mounted';
import { useScrollIntoView } from 'tradera/hooks/use-scroll-into-view';
import { Space } from 'tradera/components/atoms/space/space';

export type PopoverProps = React.PropsWithChildren<{
    icon?: React.ReactNode;
    title: string;
    text: string;
    onClose?: () => void;
    defaultOpen?: boolean;
    matchAnchorWidth?: boolean;
    scrollIntoView?: boolean;
    side?: 'top' | 'right' | 'bottom' | 'left';
    noWrap?: boolean;
}> &
    (
        | {
              buttonText: string;
              onClick: () => void;
          }
        | {
              buttonText?: never;
              onClick?: never;
          }
    );

export const Popover = ({
    children,
    icon,
    title,
    text,
    buttonText,
    onClick,
    onClose,
    defaultOpen,
    matchAnchorWidth,
    scrollIntoView,
    side,
    noWrap,
}: PopoverProps) => {
    const { t } = useTranslator();
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const scroll = useScrollIntoView();
    // We need to check for isMounted, because this component can't render on Next.js server side
    // due to the portals, both the Popover and OverlayPortal.
    const isMounted = useIsMounted();
    useEffect(() => {
        setIsOpen(defaultOpen);
        if (scrollIntoView && defaultOpen) {
            scroll('[data-popover-anchor]', { block: 'center' });
        }
    }, [defaultOpen, scroll, scrollIntoView]);

    const handleClose = () => {
        setIsOpen(false);
        onClose?.();
    };

    return (
        <OptionalWrapper wrap={!noWrap}>
            <RadixPopover.Root
                modal={true}
                open={isOpen && isMounted}
                onOpenChange={(open: boolean) => {
                    if (!open) {
                        handleClose();
                    }
                }}>
                <RadixPopover.Anchor data-popover-anchor asChild>
                    {children}
                </RadixPopover.Anchor>
                <RadixPopover.Portal>
                    <RadixPopover.Content
                        side={side}
                        className={classNames(styles.PopoverContent, {
                            [styles.PopoverContentAnchorWidth]:
                                matchAnchorWidth,
                        })}
                        collisionPadding={14}>
                        <Space gap={2} align="center" className="mb-1 pr-3">
                            {icon}
                            <Text fontWeight="heavy" font="hansen" as="p">
                                {title}
                            </Text>
                        </Space>
                        <p className="mb-1">{text}</p>
                        {buttonText && onClick && (
                            <Button theme="highlight" onClick={onClick}>
                                {buttonText}
                            </Button>
                        )}
                        <RadixPopover.Close
                            aria-label={t('tooltip_close')}
                            data-testid="tooltip-close"
                            className={classNames(
                                'btn btn-round',
                                styles.PopoverClose,
                            )}>
                            <CloseIcon className="icon" />
                        </RadixPopover.Close>
                        <RadixPopover.Arrow asChild>
                            <div className={styles.PopoverArrow} />
                        </RadixPopover.Arrow>
                    </RadixPopover.Content>
                </RadixPopover.Portal>
            </RadixPopover.Root>
            {isOpen && isMounted && <OverlayPortal onClick={handleClose} />}
        </OptionalWrapper>
    );
};

const OptionalWrapper = ({
    children,
    wrap,
}: React.PropsWithChildren<{ wrap: boolean }>) => {
    return wrap ? (
        <div className="position-relative">{children}</div>
    ) : (
        <>{children}</>
    );
};

type OverlayPortalProps = {
    onClick: () => void;
};

const OverlayPortal = ({ onClick }: OverlayPortalProps) => {
    const element = document.body.querySelector('#overlay-portal');
    if (element) {
        return createPortal(
            <div className={styles.overlay} onClick={onClick} />,
            element,
        );
    } else {
        return null;
    }
};
